import { useClient } from '@cortex/react-query';
import type { Judge, JudgeOutputSchema, UpdateJudgeInput } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import {
  JudgeEditor,
  defaultJudgeEditorState,
  type IJudgeEditorState,
} from '../../components/judges/JudgeEditor';
import { JudgeTestDrawer } from '../../components/judges/JudgeTestDrawer';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/judges/$judgeId')({ component: JudgeDetailPage });

function JudgeDetailPage() {
  const { judgeId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const judge = useQuery({
    queryKey: ['judge', judgeId],
    queryFn: () => client.http.get(`v1/judges/${judgeId}`).json<Judge>(),
  });

  const [state, setState] = useState<IJudgeEditorState>(defaultJudgeEditorState);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  // Tracks whether we've initialized the form state from the server yet.
  // We only populate ONCE per judgeId — otherwise every refetch (window
  // focus, post-save invalidation, etc.) would wipe the user's in-progress
  // edits and repaint the editor with the server snapshot.
  const [populated, setPopulated] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the "populated" flag when the user navigates to a different judge
  // — so we re-initialize the form from the new judge's data.
  useEffect(() => {
    setPopulated(false);
  }, [judgeId]);

  useEffect(() => {
    if (populated) return;
    const j = judge.data;
    if (!j || !j.currentVersion) return;
    const v = j.currentVersion;
    setState({
      name: j.name,
      description: j.description ?? '',
      judgeType: j.judgeType,
      inputSchema: v.inputSchema as Record<string, unknown>,
      outputSchema: v.outputSchema as JudgeOutputSchema,
      code: v.code ?? '',
      modelConfigId: v.modelConfigId ?? '',
      systemPrompt: v.systemPrompt ?? '',
      promptTemplate: v.promptTemplate ?? '',
    });
    setPopulated(true);
  }, [judge.data, populated]);

  const update = useMutation({
    mutationFn: () => {
      const payload: UpdateJudgeInput = {
        name: state.name,
        description: state.description.trim() || null,
        inputSchema: state.inputSchema as never,
        outputSchema: state.outputSchema,
        ...(state.judgeType === 'CODE'
          ? { code: state.code }
          : {
              modelConfigId: state.modelConfigId,
              systemPrompt: state.systemPrompt,
              promptTemplate: state.promptTemplate.trim() ? state.promptTemplate : null,
            }),
      };
      return client.http.patch(`v1/judges/${judgeId}`, { json: payload }).json<Judge>();
    },
    onSuccess: async (next) => {
      setSubmitErr(null);
      const v = next.currentVersion?.versionNumber ?? null;
      setSavedVersion(v);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSavedVersion(null), 6000);
      try {
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['judges'] }),
          qc.invalidateQueries({ queryKey: ['judge', judgeId] }),
        ]);
      } catch (err) {
        console.error(err);
      }
    },
    onError: (e) => setSubmitErr((e as Error).message),
  });

  useEffect(() => {
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const remove = useMutation({
    mutationFn: () => client.http.delete(`v1/judges/${judgeId}`).text(),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['judges'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.JUDGES });
      } catch (err) {
        console.error(err);
      }
    },
  });

  if (judge.isLoading || !populated) return <Spinner />;
  if (judge.error) {
    return (
      <div className="alert alert--danger">Failed to load: {(judge.error as Error).message}</div>
    );
  }
  if (!judge.data) return <EmptyState title="Not found" />;

  return (
    <>
      <PageHeader
        title={judge.data.name}
        actions={
          <>
            <BackButton to={routes.JUDGES} label="Judges" />
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => {
                if (confirm('Delete this judge?')) remove.mutate();
              }}
            >
              Delete
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setTestOpen(true)}
            >
              Test
            </button>
          </>
        }
      />
      <JudgeEditor state={state} setState={setState} typeLocked={true} />
      {testOpen ? (
        <JudgeTestDrawer
          judgeId={judgeId}
          judgeName={judge.data.name}
          inputSchema={state.inputSchema}
          onClose={() => setTestOpen(false)}
        />
      ) : null}
      <div
        style={{
          marginTop: 'var(--sp-4)',
          display: 'flex',
          gap: 'var(--sp-3)',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          className="btn btn--primary"
          disabled={update.isPending || !state.name.trim()}
          onClick={() => update.mutate()}
        >
          {update.isPending ? 'Saving…' : 'Save (cuts new version)'}
        </button>
        {savedVersion !== null ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 999,
              background: 'var(--status-success-bg, rgba(74, 222, 128, 0.12))',
              border: '1px solid var(--status-success, #4ade80)',
              color: 'var(--status-success, #4ade80)',
              fontWeight: 600,
              fontSize: 13,
              animation: 'fk-fade-in 160ms ease-out',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
            Saved — created version v{savedVersion}
          </div>
        ) : null}
      </div>
      {submitErr ? <div className="alert alert--danger">{submitErr}</div> : null}
    </>
  );
}
